import { motion } from "framer-motion";
import { ArrowRight, Heart, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Hero({ onDonate, onLearnMore }) {
  return (
    <section
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Background decorative shapes */}
      <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-orange-100 via-amber-50 to-transparent blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute top-40 -left-32 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-blue-100 via-slate-100 to-transparent blur-3xl opacity-50 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Text column */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-navy text-white text-xs font-bold tracking-wider uppercase mb-6">
              <span className="w-2 h-2 rounded-full bg-brand-yellow animate-pulse" />
              Serving Inland Empire Youth · 3–6PM Daily
            </div>

            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-brand-navy">
              Turning after-school hours into
              <span className="relative inline-block ml-3">
                <span className="relative z-10 text-brand-coral">future&nbsp;wins.</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-brand-yellow/60 -z-0 rounded" />
              </span>
            </h1>

            <p className="mt-6 text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
              Play 2 Fitness is a <strong className="text-brand-navy">daily program</strong>{" "}
              where kids move their bodies, sharpen their minds, find mentors, and discover real
              career paths — all in one safe space. Your donation puts the next kid in the door.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="hero-donate-btn"
                onClick={onDonate}
                size="lg"
                className="rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 h-14 text-base shadow-xl shadow-orange-500/25 hover:-translate-y-1 transition-all"
              >
                <Heart className="w-5 h-5 mr-2 fill-white" />
                Donate &amp; fund a kid
              </Button>
              <Button
                data-testid="hero-learn-btn"
                onClick={onLearnMore}
                variant="outline"
                size="lg"
                className="rounded-full border-2 border-brand-navy text-brand-navy hover:bg-brand-navy hover:text-white font-bold px-8 h-14 text-base transition-all"
              >
                See the program
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>

            {/* Trust strip */}
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-brand-coral" /> Mon – Fri, 3–6 PM
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-coral" /> 501(c)(3) nonprofit
              </div>
              <div className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-brand-coral" /> 100% of every dollar funds youth
              </div>
            </div>
          </motion.div>

          {/* Image column */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] grid grid-cols-6 grid-rows-6 gap-3">
              {/* Main image — diverse kids playing on a playground */}
              <div className="col-span-6 row-span-4 relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="/hero/hero-main.png"
                  alt="A diverse group of multi-ethnic kids — including a child in a wheelchair — playing together on a sunny Inland Empire playground"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/60 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-xl">
                  <div className="text-xs font-bold uppercase tracking-wider text-brand-coral">
                    Every weekday
                  </div>
                  <div className="font-display font-black text-xl text-brand-navy mt-1 leading-snug">
                    4 pillars. 3 hours. 1 safe space.
                  </div>
                </div>
              </div>
              {/* Adaptive sports — wheelchair athlete with peers */}
              <div className="col-span-2 row-span-2 relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/hero/hero-adaptive.png"
                  alt="A teen in a sports wheelchair playing basketball with peers"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-coral/50 to-transparent" />
                <div className="absolute bottom-1.5 left-2 right-2 text-white text-[10px] font-bold uppercase tracking-wider drop-shadow-md leading-tight">
                  Adaptive
                </div>
              </div>
              {/* Down syndrome inclusion */}
              <div className="col-span-2 row-span-2 relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/hero/hero-inclusion.png"
                  alt="A joyful child with Down syndrome participating in the program with a peer"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-yellow/50 to-transparent" />
                <div className="absolute bottom-1.5 left-2 right-2 text-white text-[10px] font-bold uppercase tracking-wider drop-shadow-md leading-tight">
                  Every ability
                </div>
              </div>
              {/* Learning / mentorship */}
              <div className="col-span-2 row-span-2 relative rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/hero/hero-learning.png"
                  alt="Diverse kids learning STEM together at an after-school table"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/50 to-transparent" />
                <div className="absolute bottom-1.5 left-2 right-2 text-white text-[10px] font-bold uppercase tracking-wider drop-shadow-md leading-tight">
                  All backgrounds
                </div>
              </div>
            </div>
            {/* Accent yellow tag */}
            <div className="absolute -top-4 -right-4 rotate-6 bg-brand-yellow px-4 py-2 rounded-xl shadow-lg font-display font-black text-brand-navy text-sm">
              OPEN TO EVERY KID
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
